import React, { useState, useEffect } from 'react';
import StudentHeader from '../../Components/StudentHeader';
import Footer from '../../Components/Footer';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import StatusBadge from '../../Components/StatusBadge';
import ProductCreationWizard from '../../Components/ProductCreationWizard';
import ConfirmDialog from '../../Components/ConfirmDialog';

const ResellerProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    discount: 0,
    category: 'general',
    stock: 0,
    image: '',
    sku: ''
  });
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    variant: 'primary'
  });

  useEffect(() => {
    fetchProducts();
  }, [filterStatus, filterCategory]);

  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm || 
      product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || product.approvalStatus === filterStatus;
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const handleBulkAction = async (action) => {
    if (selectedProducts.length === 0) {
      toast.error('Please select products first');
      return;
    }

    try {
      if (action === 'delete') {
        setConfirmConfig({
          isOpen: true,
          title: 'Delete Products',
          message: `Are you sure you want to delete ${selectedProducts.length} product(s)? This action cannot be undone.`,
          variant: 'danger',
          onConfirm: async () => {
            try {
              await Promise.all(selectedProducts.map(id => HttpInterceptor.delete(`/api/products/${id}`)));
              toast.success(`${selectedProducts.length} product(s) deleted`);
              setSelectedProducts([]);
              fetchProducts();
            } catch (err) {
              toast.error('Failed to perform bulk action');
            }
          }
        });
        return;
      } else if (action === 'activate') {
        await Promise.all(selectedProducts.map(id => HttpInterceptor.put(`/api/products/${id}`, { isActive: true })));
        toast.success(`${selectedProducts.length} product(s) activated`);
      } else if (action === 'deactivate') {
        await Promise.all(selectedProducts.map(id => HttpInterceptor.put(`/api/products/${id}`, { isActive: false })));
        toast.success(`${selectedProducts.length} product(s) deactivated`);
      }
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to perform bulk action');
    }
  };

  const exportToCSV = () => {
    const headers = ['Title', 'Price', 'Stock', 'Category', 'Status', 'Approval Status'];
    const rows = filteredProducts.map(p => [
      p.title,
      p.price,
      p.stock,
      p.category,
      p.isActive ? 'Active' : 'Inactive',
      p.approvalStatus
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Products exported to CSV');
  };

  const fetchProducts = async () => {
    try {
      const response = await HttpInterceptor.get('/api/products/my-products');
      setProducts(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleWizardSubmit = async (wizardData) => {
    try {
      if (editingProduct) {
        await HttpInterceptor.put(`/api/products/${editingProduct._id}`, wizardData);
        toast.success('Product updated successfully');
      } else {
        await HttpInterceptor.post('/api/products', wizardData);
        toast.success('Product created successfully. It is pending admin approval.');
      }
      setShowForm(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || '',
      price: product.price,
      discount: product.discount || 0,
      category: product.category || 'general',
      stock: product.stock || 0,
      image: product.image || '',
      sku: product.sku || ''
    });
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Product',
      message: 'Are you sure you want to delete this product? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await HttpInterceptor.delete(`/api/products/${id}`);
          toast.success('Product deleted successfully');
          fetchProducts();
        } catch (error) {
          toast.error('Failed to delete product');
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      discount: 0,
      category: 'general',
      stock: 0,
      image: '',
      sku: ''
    });
  };


  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <StudentHeader />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold">My Products</h1>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                resetForm();
                setEditingProduct(null);
                setShowForm(true);
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <span>+</span> Add Product
            </button>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Template Info Banner */}
        {products.length === 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> When adding your first product, you can choose from pre-filled templates for common items like yoga mats, blocks, and more. This saves time by pre-filling shipping details, return policies, and other information.
                </p>
              </div>
            </div>
          </div>
        )}

        {showForm && (
          <ProductCreationWizard
            onClose={() => {
              setShowForm(false);
              setEditingProduct(null);
              resetForm();
            }}
            onSubmit={handleWizardSubmit}
            initialData={editingProduct ? {
              title: editingProduct.title,
              description: editingProduct.description || '',
              price: editingProduct.price,
              discount: editingProduct.discount || 0,
              category: editingProduct.category || 'general',
              stock: editingProduct.stock || 0,
              image: editingProduct.image || '',
              sku: editingProduct.sku || ''
            } : null}
            isEdit={!!editingProduct}
          />
        )}

        {false && showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Discount %</label>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Stock *</label>
                  <input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({...formData, stock: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image URL</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingProduct ? 'Update' : 'Create'} Product
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 text-lg">No products found matching your filters</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                    setFilterCategory('all');
                  }}
                  className="mt-4 text-purple-600 hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              filteredProducts.map(product => (
                <div key={product._id} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProducts([...selectedProducts, product._id]);
                        } else {
                          setSelectedProducts(selectedProducts.filter(id => id !== product._id));
                        }
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      aria-label={`Select ${product.title}`}
                    />
                    <span className="text-sm text-gray-600">Select</span>
                  </div>
                  <div className="h-48 bg-gray-200">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.title} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center text-gray-400 ${product.image ? 'hidden' : ''}`}>
                      No Image
                    </div>
                  </div>
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-2">{product.title}</h3>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product.description}</p>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xl font-bold text-purple-600">₹{product.price}</span>
                    <StatusBadge status={product.approvalStatus} />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Stock: {product.stock}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product._id)}
                        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        )}
      </main>

      <Footer />

      {/* Unified Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        variant={confirmConfig.variant}
      />
    </div>
  );
};

export default ResellerProducts;

